import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Input, Modal, Form, message, Spin, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { vendorService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const Vendors = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncingId, setSyncingId] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const data = await vendorService.getAll();
      setVendors(data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      message.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <a onClick={() => navigate(`/vendors/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      render: (text) => text ? <a href={text} target="_blank" rel="noopener noreferrer">{text}</a> : 'N/A',
    },
    {
      title: 'Contact Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Total Spend',
      dataIndex: 'total_spend',
      key: 'total_spend',
      render: (value) => `$${value.toLocaleString()}`,
      sorter: (a, b) => a.total_spend - b.total_spend,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<SyncOutlined />} 
            size="small" 
            onClick={() => handleSync(record.id)}
            loading={syncingId === record.id}
          >
            Sync
          </Button>
        </Space>
      ),
    },
  ];

  const handleSync = async (id) => {
    try {
      setSyncingId(id);
      await vendorService.syncVendor(id);
      message.success('Vendor data synchronized successfully');
      fetchVendors();
    } catch (error) {
      console.error('Error syncing vendor data:', error);
      message.error('Failed to sync vendor data');
    } finally {
      setSyncingId(null);
    }
  };

  const handleAddVendor = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      await vendorService.create(values);
      message.success('Vendor added successfully');
      
      setIsModalVisible(false);
      form.resetFields();
      fetchVendors();
    } catch (error) {
      console.error('Error adding vendor:', error);
      message.error('Failed to add vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredVendors = vendors.filter(vendor => 
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Vendors</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddVendor}
        >
          Add Vendor
        </Button>
      </div>
      
      <Card>
        <Input 
          placeholder="Search vendors" 
          prefix={<SearchOutlined />} 
          style={{ width: 300, marginBottom: 16 }}
          value={searchTerm}
          onChange={handleSearch}
        />
        
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredVendors}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      <Modal
        title="Add New Vendor"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Vendor Name"
            rules={[{ required: true, message: 'Please enter vendor name' }]}
          >
            <Input placeholder="Enter vendor name" />
          </Form.Item>
          <Form.Item
            name="website"
            label="Website"
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Describe the vendor" />
          </Form.Item>
          <Form.Item
            name="contact_email"
            label="Contact Email"
          >
            <Input type="email" placeholder="contact@example.com" />
          </Form.Item>
          <Form.Item
            name="contact_phone"
            label="Contact Phone"
          >
            <Input placeholder="555-123-4567" />
          </Form.Item>
          <Form.Item
            name="total_spend"
            label="Total Spend"
          >
            <Input prefix="$" type="number" placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Vendors;